/**
 * DevConsole — the rebuilt floating dev panel.
 *
 * Replaces the older 460px `DebugPanel`. The shape is intentionally wider:
 * an always-visible top status bar + a left sidebar with grouped sections.
 * The original tabs (Chain / Token / Wallet / Client / Stream / Stage) all
 * live inside the new layout, so anything the old panel could do is still
 * one click away.
 *
 * Visibility & toggling (see useDebugVisible — build-time gated for mainnet):
 *   - Available only in `import.meta.env.DEV`, or a prod build explicitly built
 *     with `VITE_ENABLE_DEV_CONSOLE=1`. A default prod build returns `null`.
 *   - When available: enabled via `?debug=1` and toggled with backtick (` ).
 *   - This panel exposes raw ix buttons — use the admin-gated `/ops` dashboard
 *     for mainnet operations, never this.
 */
import React, { useMemo, useState } from "react";

import { ChainTab } from "./tabs/ChainTab";
import { ClientTab } from "./tabs/ClientTab";
import { StageTab } from "./tabs/StageTab";
import { StreamTab } from "./tabs/StreamTab";
import { TokenTab } from "./tabs/TokenTab";
import { WalletTab } from "./tabs/WalletTab";
import { OverviewSection } from "./sections/OverviewSection";
import { DevWalletsSection } from "./sections/DevWalletsSection";
import { ChatModSection } from "./sections/ChatModSection";
import { PlayersSection } from "./sections/PlayersSection";
import { ErrorsSection } from "./sections/ErrorsSection";
import { TokenStubsSection } from "./sections/TokenStubsSection";
import { McapFlywheelSection } from "./sections/McapFlywheelSection";
import { LaunchCheckSection } from "./sections/LaunchCheckSection";
import { DevConsoleStatusBar } from "./DevConsoleStatusBar";
import { useDebugVisible } from "./useDebugVisible";

type SectionId =
  | "overview"
  | "launchCheck"
  | "devWallets"
  | "chain"
  | "chatMod"
  | "players"
  | "errors"
  | "wallet"
  | "token"
  | "tokenStubs"
  | "mcapFlywheel"
  | "client"
  | "stream"
  | "stage";

interface SectionDef {
  id: SectionId;
  label: string;
  group: "dashboard" | "test" | "live" | "extras";
  Component: () => JSX.Element;
}

const SECTIONS: SectionDef[] = [
  { id: "overview",   label: "Overview",     group: "dashboard", Component: OverviewSection },
  { id: "errors",     label: "Errors",       group: "dashboard", Component: ErrorsSection },
  { id: "launchCheck", label: "Launch Check", group: "test",     Component: LaunchCheckSection },
  { id: "devWallets", label: "Dev Wallets",  group: "test",      Component: DevWalletsSection },
  { id: "chain",      label: "Chain (ix)",   group: "test",      Component: ChainTab },
  { id: "mcapFlywheel", label: "Mcap Flywheel", group: "test",    Component: McapFlywheelSection },
  { id: "tokenStubs", label: "Token Stubs",  group: "test",      Component: TokenStubsSection },
  { id: "chatMod",    label: "Chat / Mod",   group: "live",      Component: ChatModSection },
  { id: "players",    label: "Players",      group: "live",      Component: PlayersSection },
  { id: "wallet",     label: "Wallet",       group: "extras",    Component: WalletTab },
  { id: "token",      label: "Token",        group: "extras",    Component: TokenTab },
  { id: "client",     label: "Client",       group: "extras",    Component: ClientTab },
  { id: "stream",     label: "Stream",       group: "extras",    Component: StreamTab },
  { id: "stage",      label: "Stage",        group: "extras",    Component: StageTab },
];

const GROUP_LABELS: Record<SectionDef["group"], string> = {
  dashboard: "DASHBOARD",
  test: "TEST / DRIVE",
  live: "LIVE STATE",
  extras: "EXTRAS",
};

export function DevConsole(): JSX.Element | null {
  const visible = useDebugVisible();
  const [active, setActive] = useState<SectionId>("overview");

  const grouped = useMemo(() => {
    const groups: { group: SectionDef["group"]; items: SectionDef[] }[] = [];
    for (const def of SECTIONS) {
      let g = groups.find((x) => x.group === def.group);
      if (!g) {
        g = { group: def.group, items: [] };
        groups.push(g);
      }
      g.items.push(def);
    }
    return groups;
  }, []);

  if (!visible) return null;

  const ActiveComponent =
    SECTIONS.find((s) => s.id === active)?.Component ?? OverviewSection;

  return (
    <div style={consoleStyle} role="dialog" aria-label="Dev console">
      <DevConsoleStatusBar />
      <div style={mainRowStyle}>
        <nav style={sidebarStyle} aria-label="Dev console sections">
          {grouped.map((grp) => (
            <div key={grp.group} style={{ marginBottom: 8 }}>
              <div style={groupLabelStyle}>{GROUP_LABELS[grp.group]}</div>
              {grp.items.map((def) => {
                const isActive = def.id === active;
                return (
                  <button
                    key={def.id}
                    onClick={() => setActive(def.id)}
                    style={navBtnStyle(isActive)}
                  >
                    {def.label}
                  </button>
                );
              })}
            </div>
          ))}
          <div style={footerHintStyle}>
            <code style={kbdStyle}>`</code> to toggle
          </div>
        </nav>
        <main style={bodyStyle}>
          <ActiveComponent />
        </main>
      </div>
    </div>
  );
}

const consoleStyle: React.CSSProperties = {
  position: "fixed",
  top: 12,
  left: 12,
  width: 880,
  height: "calc(100vh - 24px)",
  background: "#08080d",
  border: "1px solid #2a2a36",
  borderRadius: 10,
  color: "#e8e8ef",
  fontFamily:
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
  display: "flex",
  flexDirection: "column",
  zIndex: 1000,
  boxShadow: "0 14px 40px rgba(0,0,0,0.6)",
  overflow: "hidden",
};

const mainRowStyle: React.CSSProperties = {
  display: "flex",
  flex: "1 1 auto",
  minHeight: 0,
};

const sidebarStyle: React.CSSProperties = {
  width: 160,
  flex: "0 0 auto",
  background: "#0a0a12",
  borderRight: "1px solid #1c1c26",
  padding: "10px 6px",
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
};

const groupLabelStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 1.5,
  color: "#5a5a72",
  padding: "6px 8px 4px",
};

function navBtnStyle(active: boolean): React.CSSProperties {
  return {
    display: "block",
    width: "100%",
    padding: "6px 10px",
    margin: "1px 0",
    background: active ? "#1c2433" : "transparent",
    color: active ? "#e8e8ef" : "#9aa0b4",
    border: "none",
    borderLeft: active ? "2px solid #4a8eff" : "2px solid transparent",
    borderRadius: 4,
    textAlign: "left",
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
  };
}

const footerHintStyle: React.CSSProperties = {
  marginTop: "auto",
  padding: "8px 10px",
  fontSize: 10,
  color: "#5a5a72",
};

const kbdStyle: React.CSSProperties = {
  padding: "1px 4px",
  background: "#1c1c26",
  border: "1px solid #2a2a36",
  borderRadius: 3,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  color: "#cfd2dc",
};

const bodyStyle: React.CSSProperties = {
  flex: "1 1 auto",
  overflowY: "auto",
  minHeight: 0,
  background: "#08080d",
};
